import { ChangeDetectorRef, Component, ElementRef, inject, Input, OnDestroy, ViewChild } from '@angular/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { ControlValueAccessor, FormBuilder, NgControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { firstValueFrom, Subject } from 'rxjs';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { filter, map, mergeMap, startWith } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';

/**
 * How far Annex I of Reg. (EU) 2023/1115 commits for a code, as resolved by hscode-service.
 * `IN_SCOPE`: the Annex row has no `ex` prefix, so the code and everything below it are
 * covered. `PARTIAL`: the row is an `ex` row and covers only the articles made of the
 * commodity — the code alone cannot decide it. Absent/null: not covered by the EUDR.
 */
export type EudrScope = 'IN_SCOPE' | 'PARTIAL';

export interface HsCode {
  code: string;
  description: string;
  level: 'CHAPTER' | 'HEADING' | 'SUBHEADING' | 'CN_CODE';
  parentCode?: string;
  supplementaryUnit?: string;
  validFrom: string;
  validTo?: string;
  tags?: string[];
  eudrScope?: EudrScope | null;
  /** The Annex I row the scope came from, verbatim (`ex 9401`). */
  eudrScopeSource?: string | null;
}

/** A translation key plus its interpolation params, rendered as a hint under the field. */
export interface HsCodeHint {
  key: string;
  params?: Record<string, string>;
}

export interface HscodeSearchResult {
  query: string;
  totalResults: number;
  results: HsCode[];
  page: number;
  pageSize: number;
}

const HSCODE_SERVICE = 'https://hscode-service.supplycanvas.com';

@Component({
  selector: 'hscode-selector',
  templateUrl: './hscode-selector.component.html',
  styleUrl: './hscode-selector.component.scss',
  exportAs: 'hscodeSelector',
  providers: [{ provide: MatFormFieldControl, useExisting: HsCodeSelectorComponent }],
  host: {
    '[id]': 'id',
    '(focusin)': 'onFocusIn()',
    '(focusout)': 'onFocusOut($event)',
    role: 'combobox',
  },
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatIconModule,
    MatInputModule,
    MatOptionModule,
    MatCheckboxModule,
    TranslateModule,
  ],
})
export class HsCodeSelectorComponent implements ControlValueAccessor, MatFormFieldControl<string | null>, OnDestroy {
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly http = inject(HttpClient);
  readonly ngControl = inject(NgControl, { optional: true, self: true });

  /**
   * EU TRACES `HSHeadingType` (EUDR XSD, common/v3): 2 to 6 digits, maxLength 6. A longer code
   * is rejected by the EU server with a raw JAXB fault, so it must never be submitted as is.
   */
  private static readonly TRACES_HS_PATTERN = /^[0-9]{2,6}$/;

  /**
   * An 8-digit CN or 10-digit TARIC code: the server submits its first six digits (app#393,
   * `TracesDdsService.hsHeadingForTraces`). Digits 7+ are the EU's own refinement, which TRACES
   * does not know, so the cut loses nothing — the field only announces it, it does not flag it.
   */
  private static readonly LONGER_THAN_HS = /^[0-9]{7,}$/;

  static nextId = 0;
  selectedHsCode: HsCode | null = null;
  /** True once the details for the current code have come back — scope claims wait for it. */
  private resolved = false;
  /** True when the nomenclature actually has the current code. */
  private codeKnown = false;
  showEudrOnly = true;
  stateChanges = new Subject<void>();
  focused = false;
  touched = false;
  controlType = 'hscode-selector';
  id = `hscode-selector-${HsCodeSelectorComponent.nextId++}`;
  shouldLabelFloat = true;
  searchControl = new FormBuilder().nonNullable.control('');

  @ViewChild('inputRef') inputRef!: ElementRef<HTMLInputElement>; // new added
  @ViewChild(MatAutocompleteTrigger) autoTrigger!: MatAutocompleteTrigger;
  @ViewChild('chipRef') chipRef?: ElementRef<HTMLElement>;

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  searchResults = this.searchControl.valueChanges.pipe(
    startWith<string | any>(''),
    filter((value) => typeof value === 'string'),
    map((searchTerm) => {
      // If user is typing and the searchTerm is different from selected code, allow search
      const isSearching = !this.selectedHsCode || searchTerm !== this.selectedHsCode.code;
      return isSearching && searchTerm.length >= 2 ? searchTerm : null;
    }),
    filter((searchTerm) => searchTerm !== null),
    mergeMap(async (searchTerm: string) => {
      try {
        const params = new URLSearchParams({ query: searchTerm });
        if (this.showEudrOnly) {
          params.set('scope', 'eudr');
        }
        const url = `${HSCODE_SERVICE}/api/hscode/search?${params.toString()}`;
        const response = await firstValueFrom(this.http.get<HscodeSearchResult>(url));
        return response.results || [];
      } catch (error: any) {
        console.error('Error searching HS codes:', error);
        return [];
      }
    })
  );

  get empty() {
    return !this.selectedHsCode;
  }

  private updateDisplayValue() {
    if (this.selectedHsCode) {
      this.searchControl.setValue(this.selectedHsCode.code, { emitEvent: false });
    } else {
      this.searchControl.setValue('', { emitEvent: false });
    }
  }

  private _placeholder = '';

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }

  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }

  private _enforceTracesLength = false;

  /**
   * Set on the EUDR/DDS form only. Elsewhere a longer CN code is normal and wanted — customs
   * clearance works at 8 digits (Gunther Kuepper, 2026-09-11) — and must not be flagged.
   */
  @Input()
  get enforceTracesLength(): boolean {
    return this._enforceTracesLength;
  }

  set enforceTracesLength(value: BooleanInput) {
    this._enforceTracesLength = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  private _required = false;

  @Input()
  get required(): boolean {
    return this._required || !!this.ngControl?.control?.hasValidator(Validators.required);
  }

  set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  private _disabled = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  @Input()
  get value(): string | null {
    return this.selectedHsCode?.code || null;
  }

  set value(hsCode: string | null) {
    if (hsCode && hsCode.trim()) {
      // Set the input value immediately for custom codes
      this.searchControl.setValue(hsCode.trim(), { emitEvent: false });

      // Defer async operations to next tick to avoid ExpressionChangedAfterItHasBeenCheckedError
      Promise.resolve().then(() => this.resolve(hsCode.trim()));
    } else {
      this.selectedHsCode = null;
      this.resolved = false;
      this.codeKnown = false;
      this.updateDisplayValue();
      this.stateChanges.next();
    }
  }

  /**
   * Loads a code's details so the hints below the field are based on data rather than on the
   * absence of it. Both entry paths go through here: the blur path used to build a tagless
   * placeholder instead, which made every hand-typed code — including a perfectly valid one —
   * look like it was outside the EUDR scope.
   */
  private async resolve(code: string) {
    this.resolved = false;
    const { hsCode, known } = await this.fetchHsCodeDetails(code);

    // A newer edit may have superseded this lookup while it was in flight.
    if (this.searchControl.value?.trim() !== code) return;

    this.selectedHsCode = hsCode;
    this.codeKnown = known;
    this.resolved = true;
    this.updateDisplayValue();
    this.stateChanges.next();
    this.changeDetectorRef.markForCheck();
  }

  /**
   * @returns the code's nomenclature entry and whether it was actually found. `known: false`
   *   means the code is not in the nomenclature or the service was unreachable — in both cases
   *   we know nothing about its EUDR scope and must not claim it is out of scope.
   */
  private async fetchHsCodeDetails(code: string): Promise<{ hsCode: HsCode; known: boolean }> {
    const unknown = {
      hsCode: {
        code: code,
        description: '',
        level: 'CN_CODE' as const,
        validFrom: new Date().toISOString().split('T')[0],
      },
      known: false,
    };

    try {
      // Try searching for the exact code
      const url = `${HSCODE_SERVICE}/api/hscode/search?query=${encodeURIComponent(code)}`;
      const response = await firstValueFrom(this.http.get<HscodeSearchResult>(url));

      // Find exact match first
      const exactMatch = response.results.find((h) => h.code === code);
      if (exactMatch) {
        return { hsCode: exactMatch, known: true };
      }

      // A prefix or description hit is not this code — its scope says nothing about ours.
      return unknown;
    } catch (error) {
      console.warn(`Failed to fetch HS code details for: ${code}`, error);
      return unknown;
    }
  }

  /**
   * Paints the field red. A TRACES-unsubmittable code counts, but deliberately WITHOUT adding a
   * validator to the FormControl: the DDS form disables its save button on `form.invalid`, and
   * a draft must stay savable while the code is still being sorted out (E2a, 2026-09-12 —
   * narrowing nothing about `required`, which F1b already settled). Submission is refused in the
   * confirm dialog and, finally, by TracesDdsService.submit.
   */
  get errorState(): boolean {
    const controlInvalid = !!this.ngControl?.control?.invalid && this.touched;
    const tracesInvalid = this.enforceTracesLength && !this.tracesSubmittable;
    return controlInvalid || tracesInvalid;
  }

  setDescribedByIds = () => void 0;
  onContainerClick = () => void 0;
  onChange = (value: string | null) => void 0;
  onTouched = () => void 0;
  displayHsCode = (hsCode: HsCode | null): string => (hsCode ? hsCode.code : '');

  selectHsCode(hsCode: HsCode) {
    this.selectedHsCode = { ...hsCode };
    // The autocomplete option came from the service, so it is resolved by construction.
    this.resolved = true;
    this.codeKnown = true;
    this.updateDisplayValue();
    this.onChange(hsCode.code);

    try {
      this.autoTrigger?.closePanel();
    } catch {
      // ignore close panel failures
    }

    requestAnimationFrame(() => {
      if (this.chipRef?.nativeElement) {
        this.chipRef.nativeElement.focus();
      } else if (this.inputRef?.nativeElement) {
        this.inputRef.nativeElement.blur();
      }
    });

    this.touched = true;
    this.onTouched();
    this.stateChanges.next();

    this.changeDetectorRef.detectChanges();
  }

  clearSelection(event: Event) {
    event.stopPropagation();
    this.selectedHsCode = null;
    this.searchControl.setValue('', { emitEvent: false });
    this.onChange(null);
    setTimeout(() => this.inputRef?.nativeElement?.focus(), 0);
    this.stateChanges.next();
  }

  onInputBlur() {
    // Clicking an option blurs the input on mousedown, BEFORE the option's click. Committing the
    // typed text here rendered the detail block, the panel moved under the cursor and the click
    // missed — the first pick after typing was lost. While the panel is open, the pick (or
    // onPanelClosed, when there is none) decides instead.
    if (this.autoTrigger?.panelOpen) return;
    this.commitTypedValue();
  }

  /** The panel closed without keeping focus: an outside click after typing still commits. */
  onPanelClosed() {
    if (document.activeElement !== this.inputRef?.nativeElement) {
      this.commitTypedValue();
    }
  }

  private commitTypedValue() {
    // If user typed something but didn't select from autocomplete, resolve it against the
    // service — the value is a real HS code, not a placeholder without scope information.
    const currentValue = this.searchControl.value?.trim();
    if (currentValue && this.selectedHsCode?.code !== currentValue) {
      this.selectedHsCode = { code: currentValue, description: '' } as HsCode;
      this.resolved = false;
      this.codeKnown = false;
      this.onChange(currentValue);
      this.stateChanges.next();
      void this.resolve(currentValue);
    }
  }

  /** The resolved EUDR scope of the selected code, or null when it is not covered. */
  get eudrScope(): EudrScope | null {
    return this.selectedHsCode?.eudrScope ?? null;
  }

  /**
   * Whether the service answered on scope at all. It sends `eudrScope: null` for a code outside
   * Annex I and omits the key entirely before the scope release, so `'eudrScope' in entry`
   * separates "decided: not covered" from "cannot say" across a deploy-order gap.
   */
  private get scopeReported(): boolean {
    return !!this.selectedHsCode && 'eudrScope' in this.selectedHsCode;
  }

  /**
   * False when the selected code cannot reach EU TRACES at all: it neither matches
   * TRACES_HS_PATTERN nor is a longer all-digit code the server trims to six on submit.
   */
  get tracesSubmittable(): boolean {
    const code = this.selectedHsCode?.code;
    return (
      !code ||
      HsCodeSelectorComponent.TRACES_HS_PATTERN.test(code) ||
      HsCodeSelectorComponent.LONGER_THAN_HS.test(code)
    );
  }

  /**
   * What to tell the user about the selected code, as translation keys. Length and scope are
   * independent: an 8-digit code can be squarely in scope and still be unsubmittable, which
   * the single "not valid for EUDR-traces" message used to conflate.
   */
  get hints(): HsCodeHint[] {
    const code = this.selectedHsCode?.code;
    if (!code) return [];

    const hints: HsCodeHint[] = [];

    if (this.enforceTracesLength) {
      if (HsCodeSelectorComponent.LONGER_THAN_HS.test(code)) {
        hints.push({ key: 'hs_code_trimmed_for_traces_tr', params: { shortened: code.slice(0, 6) } });
      } else if (!this.tracesSubmittable) {
        hints.push({ key: 'hs_code_invalid_for_traces_tr' });
      }
    }

    // Say nothing about scope until the lookup came back, so a slow service does not flash
    // "not covered by the EUDR" at a code that is.
    if (!this.resolved) return hints;

    if (!this.codeKnown) {
      hints.push({ key: 'hs_code_unknown_tr' });
    } else if (!this.scopeReported) {
      // Service predates eudrScope (it omits the key rather than sending null). Fall back to
      // the exact EUDR tag: that is the old, incomplete answer, but announcing "not covered by
      // the EUDR" for every code because the field is missing would be far worse.
      if (!(this.selectedHsCode?.tags ?? []).some((tag) => tag?.toUpperCase?.() === 'EUDR')) {
        hints.push({ key: 'hs_code_not_in_eudr_scope_tr' });
      }
    } else if (this.eudrScope === null) {
      hints.push({ key: 'hs_code_not_in_eudr_scope_tr' });
    } else if (this.eudrScope === 'PARTIAL') {
      hints.push({
        key: 'hs_code_eudr_partial_tr',
        params: { annex: this.selectedHsCode?.eudrScopeSource ?? code },
      });
    }

    return hints;
  }

  toggleEudrOnly(checked: boolean) {
    this.showEudrOnly = checked;
    const currentValue = this.searchControl.value ?? '';
    this.searchControl.setValue(currentValue, { emitEvent: true });
  }

  registerOnChange = (fn: any) => (this.onChange = fn);
  registerOnTouched = (fn: any) => (this.onTouched = fn);
  ngOnDestroy = () => this.stateChanges.complete();

  onFocusIn() {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent) {
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  writeValue = (hsCode: string | null) => (this.value = hsCode);
  setDisabledState = (isDisabled: boolean) => (this.disabled = isDisabled);

  focusInput(ev?: Event) {
    ev?.stopPropagation?.();
    ev?.preventDefault?.();
    queueMicrotask(() => this.inputRef?.nativeElement?.focus());
  }
}
